import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3, BookOpen, CheckCircle2, Clock, FolderPlus, HelpCircle, Pencil, Plus, Trash2, Upload,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppBadge, AppButton, AppDataTable, ConfirmModal, EmptyState, FilterToolbar, IconButton, PageHeader,
  SearchInput, SegmentedControl, StatsCard, StatsGrid, isColumnFilterActive,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import AddQuestionsDialog from "./AddQuestionsDialog";
import QuestionFolders from "./QuestionFolders";
import QuizCategoryFormModal from "./QuizCategoryFormModal";
import SetupSteps from "./SetupSteps";
import {
  DEFAULT_SORT, DIFFICULTY_LABELS, QUESTION_TYPE_LABELS, attemptsLine, bankSteps, canUseQuizAi, currentUsertype,
  listParams, plural, questionPath, splitCategories,
} from "./quizBankRules";
import { bankTrail } from "./quizCenterTrail";

const PAGE_SIZE = 20;
const DIFFICULTY_TONE = { easy: "emerald", medium: "amber", hard: "rose" };
const dateOf = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

/**
 * QuizQuestionsPage — the question bank: `/quiz-bank`, and the platform
 * library's `/sa/marketplace/quiz-questions`.
 *
 * Two views of the same questions. Folders (the default) walks category →
 * subtopic → questions, and is where AI drafting and the library's publishing
 * live. List is every question in one table, filtered from its column headers
 * and searched from the toolbar inside the table card — filtered on the
 * server, so a filter covers the whole bank and not one page of it.
 *
 * A bank with no questions yet leads with its three setup steps; "Add
 * questions" always asks HOW first (AddQuestionsDialog).
 */
export default function QuizQuestionsPage() {
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank";
  const canUseAi = useMemo(() => canUseQuizAi(currentUsertype()), []);

  const [view, setView] = useState("folders");
  const [filterData, setFilterData] = useState({ categories: [], difficulties: {}, totalQuestions: 0, loaded: false });
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);

  // { category, parentId } while the category dialog is open (null = a new one).
  const [categoryForm, setCategoryForm] = useState(null);
  const [foldersVersion, setFoldersVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [folderPath, setFolderPath] = useState({ category: "", subcategory: "" });
  const onPathChange = useCallback((p) => setFolderPath((prev) => (
    prev.category === p.category && prev.subcategory === p.subcategory ? prev : p
  )), []);

  // ── The list view ──
  const [rows, setRows] = useState([]);
  const [listState, setListState] = useState({ loading: false, total: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [columnFilters, setColumnFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/quiz-questions/filters");
      setFilterData({ ...data, loaded: true });
    } catch {
      setFilterData((f) => ({ ...f, loaded: true }));
    }
  }, []);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  // The search is sent once typing pauses, not on every key.
  useEffect(() => {
    const next = searchText.trim();
    if (next === search) return undefined;
    const t = setTimeout(() => { setSearch(next); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchText, search]);

  const fetchRows = useCallback(async () => {
    setListState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await apiClient.get("/quiz-questions", {
        params: listParams({ page, pageSize: PAGE_SIZE, search, sort, columnFilters }),
      });
      setRows(data.questions || []);
      setListState({ loading: false, total: data.total || 0, totalPages: data.totalPages || 0 });
    } catch {
      setRows([]);
      setListState({ loading: false, total: 0, totalPages: 0 });
      notify("The questions could not be loaded.", "error");
    }
  }, [page, search, sort, columnFilters, notify]);

  useEffect(() => { if (view === "list") fetchRows(); }, [view, fetchRows]);

  const refreshAll = () => {
    fetchFilters();
    setFoldersVersion((v) => v + 1);
    if (view === "list") fetchRows();
  };

  const { top, subs } = useMemo(() => splitCategories(filterData.categories), [filterData.categories]);
  const total = filterData.totalQuestions || 0;
  const guide = bankSteps({ categories: top.length, subtopics: subs.length, questions: total });
  const showGuide = filterData.loaded && total === 0;
  // Nothing to browse yet: the steps are the whole page.
  const bankIsEmpty = showGuide && top.length === 0;

  const filtering = !!search || Object.values(columnFilters).some(isColumnFilterActive);
  const clearFilters = () => { setSearchText(""); setSearch(""); setColumnFilters({}); setPage(1); };

  const handleCategorySaved = (_saved, { created }) => {
    setCategoryForm(null);
    notify(created ? "Category created" : "Category updated");
    fetchFilters();
    setFoldersVersion((v) => v + 1);
  };

  const handleDelete = async () => {
    const q = deleteTarget;
    setDeleteTarget(null);
    if (!q) return;
    try {
      await apiClient.delete(`/quiz-questions/${q._id}`);
      notify("Question deleted");
      setSelected((ids) => ids.filter((id) => id !== q._id));
      refreshAll();
    } catch (err) {
      notify(err.response?.data?.message || "The question could not be deleted.", "error");
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await apiClient.post("/quiz-questions/bulk-delete", { ids: selected });
      notify(data.message || `${plural(selected.length, "question")} deleted`);
      setSelected([]);
      refreshAll();
    } catch (err) {
      notify(err.response?.data?.message || "The questions could not be deleted.", "error");
    }
    setBulkDeleting(false);
    setBulkConfirm(false);
  };

  const columns = useMemo(() => [
    {
      key: "question",
      label: "Question",
      minWidth: 280,
      render: (q) => (
        <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <span className="tw-line-clamp-2 tw-text-[0.8125rem] tw-text-content">{q.questionText}</span>
          {(q.passage?.title || q.tags?.length > 0) && (
            <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
              {q.passage?.title && (
                <AppBadge size="sm" variant="outline" title={`About the passage “${q.passage.title}”`}>
                  <BookOpen size={11} aria-hidden="true" /> {q.passage.title.length > 32 ? `${q.passage.title.slice(0, 32)}…` : q.passage.title}
                </AppBadge>
              )}
              {(q.tags || []).slice(0, 3).map((t) => <AppBadge key={t} size="sm" variant="secondary">{t}</AppBadge>)}
              {q.tags?.length > 3 && <span className="tw-text-xs tw-text-content-subtle">+{q.tags.length - 3}</span>}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "category",
      label: "Topic",
      filter: { type: "set", options: top.map((c) => ({ value: String(c._id), label: c.name })) },
      render: (q) => <span className="tw-text-[0.8125rem] tw-text-content-muted">{questionPath(q) || "—"}</span>,
    },
    {
      key: "questionType",
      label: "Type",
      filter: { type: "set", options: Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => ({ value, label })) },
      render: (q) => <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-text-content">{QUESTION_TYPE_LABELS[q.questionType] || q.questionType}</span>,
    },
    {
      key: "difficulty",
      label: "Difficulty",
      filter: { type: "set", options: Object.entries(DIFFICULTY_LABELS).map(([value, label]) => ({ value, label })) },
      render: (q) => (q.difficulty
        ? <AppBadge size="sm" color={DIFFICULTY_TONE[q.difficulty]}>{DIFFICULTY_LABELS[q.difficulty] || q.difficulty}</AppBadge>
        : "—"),
    },
    {
      key: "marks",
      label: "Marks",
      align: "right",
      render: (q) => (
        <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-tabular-nums tw-text-content">
          {q.marks}
          {q.negativeMarks > 0 && <span className="tw-text-danger"> (−{q.negativeMarks})</span>}
        </span>
      ),
    },
    {
      key: "attempts",
      label: "Answered",
      sortable: true,
      render: (q) => <span className="tw-text-xs tw-text-content-muted">{attemptsLine(q) || "Not yet"}</span>,
    },
    {
      key: "createdAt",
      label: "Added",
      sortable: true,
      render: (q) => <span className="tw-whitespace-nowrap tw-text-xs tw-text-content-muted">{dateOf(q.createdAt)}</span>,
    },
    {
      key: "actions",
      label: "",
      pinned: "right",
      align: "right",
      render: (q) => (
        <span className="tw-inline-flex tw-gap-0.5">
          <IconButton icon={Pencil} label={`Edit “${q.questionText?.slice(0, 40) || "question"}”`} size="sm" onClick={(e) => { e.stopPropagation(); navigate(`${base}/edit/${q._id}`); }} />
          <IconButton icon={Trash2} label="Delete question" size="sm" variant="ghost-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(q); }} />
        </span>
      ),
    },
  ], [top, base, navigate]);

  const toolbar = (
    <FilterToolbar
      flush
      search={(
        <SearchInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search question text, tags or source…"
        />
      )}
      actions={selected.length > 0 ? (
        <>
          <span className="tw-text-[0.8125rem] tw-font-medium tw-text-content">{selected.length} selected</span>
          <AppButton variant="ghost" size="sm" onClick={() => setSelected([])}>Clear</AppButton>
          <AppButton variant="destructive" size="sm" onClick={() => setBulkConfirm(true)}>
            <Trash2 size={14} aria-hidden="true" /> Delete selected
          </AppButton>
        </>
      ) : (filtering && <AppButton variant="ghost" size="sm" onClick={clearFilters}>Clear filters</AppButton>)}
    />
  );

  const headerActions = (
    <>
      <AppButton variant="outline" onClick={() => setCategoryForm({ category: null })}>
        <FolderPlus size={16} aria-hidden="true" /> New category
      </AppButton>
      <AppButton variant="outline" onClick={() => navigate(`${base}/passages`)}>
        <BookOpen size={16} aria-hidden="true" /> Passages
      </AppButton>
      <AppButton onClick={() => setAdding(true)}>
        <Plus size={16} aria-hidden="true" /> Add questions
      </AppButton>
    </>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        breadcrumbs={bankTrail({ isLibrary, categories: filterData.categories, ...(view === "folders" ? folderPath : {}) })}
        icon={HelpCircle}
        title="Question bank"
        description={total > 0
          ? `${plural(total, "question")} in ${plural(top.length, "category", "categories")}`
          : "Every question your assessments draw from"}
        actions={headerActions}
      />

      {showGuide ? (
        <SetupSteps
          title="Start your question bank"
          description="Three steps. Each ticks itself off as you go."
          steps={guide.steps}
          current={guide.current}
          actions={{
            category: (
              <AppButton size="sm" onClick={() => setCategoryForm({ category: null })}>
                <FolderPlus size={14} aria-hidden="true" /> Create a category
              </AppButton>
            ),
            subtopic: (
              <AppButton size="sm" onClick={() => setCategoryForm({ category: null, parentId: top[0]?._id })}>
                <Plus size={14} aria-hidden="true" /> Add a subtopic{top.length === 1 ? ` to ${top[0].name}` : ""}
              </AppButton>
            ),
            questions: (
              <>
                <AppButton size="sm" onClick={() => setAdding(true)}>
                  <Plus size={14} aria-hidden="true" /> Add questions
                </AppButton>
                <AppButton size="sm" variant="outline" onClick={() => navigate(`${base}/bulk-upload`)}>
                  <Upload size={14} aria-hidden="true" /> Upload a spreadsheet
                </AppButton>
              </>
            ),
          }}
        />
      ) : (
        <StatsGrid>
          <StatsCard title="Questions" value={total} icon={HelpCircle} iconColor="blue" />
          <StatsCard title="Easy" value={filterData.difficulties?.easy || 0} icon={CheckCircle2} iconColor="emerald" />
          <StatsCard title="Medium" value={filterData.difficulties?.medium || 0} icon={Clock} iconColor="amber" />
          <StatsCard title="Hard" value={filterData.difficulties?.hard || 0} icon={BarChart3} iconColor="rose" />
        </StatsGrid>
      )}

      {!bankIsEmpty && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <SegmentedControl
            label="View"
            value={view}
            onChange={(v) => { setView(v); setSelected([]); }}
            options={[{ value: "folders", label: "Folders" }, { value: "list", label: "All questions" }]}
          />
          {view === "list" && (
            <span className="tw-text-[0.8125rem] tw-text-content-muted">Filter from the column headers.</span>
          )}
        </div>
      )}

      {bankIsEmpty ? null : view === "folders" ? (
        <QuestionFolders
          reloadKey={foldersVersion}
          canUseAi={canUseAi}
          onAddCategory={() => setCategoryForm({ category: null })}
          onAddSubtopic={(cat) => setCategoryForm({ category: null, parentId: cat?._id || null })}
          onEditFolder={(folder) => setCategoryForm({ category: folder })}
          onNotify={notify}
          onPathChange={onPathChange}
        />
      ) : (
        <AppDataTable
          fill={false}
          columns={columns}
          rows={rows}
          rowKey={(q) => q._id}
          onRowClick={(q) => navigate(`${base}/edit/${q._id}`)}
          loading={listState.loading}
          toolbar={toolbar}
          sort={sort}
          onSortChange={(key, direction) => { setSort({ key, direction }); setPage(1); }}
          columnFilters={columnFilters}
          onColumnFilterChange={(key, value) => { setColumnFilters((f) => ({ ...f, [key]: value })); setPage(1); }}
          selectable
          selectedRows={selected}
          onSelectionChange={setSelected}
          pagination={{ page, totalPages: listState.totalPages, total: listState.total, pageSize: PAGE_SIZE, onChange: setPage, infoLabel: "questions" }}
          emptyState={filtering ? (
            <EmptyState
              icon={HelpCircle}
              title="No questions match"
              description="Change the search or the column filters."
              action={<AppButton variant="outline" onClick={clearFilters}>Clear filters</AppButton>}
            />
          ) : (
            <EmptyState
              icon={HelpCircle}
              title="No questions yet"
              description="Add them one at a time, from a spreadsheet, or with AI."
              action={<AppButton onClick={() => setAdding(true)}><Plus size={16} aria-hidden="true" /> Add questions</AppButton>}
            />
          )}
        />
      )}

      <AddQuestionsDialog
        show={adding}
        onHide={() => setAdding(false)}
        base={base}
        canUseAi={canUseAi}
        categories={filterData.categories || []}
        defaults={view === "folders" ? folderPath : {}}
        onAiSaved={(message) => { notify(message); refreshAll(); }}
      />

      <QuizCategoryFormModal
        show={!!categoryForm}
        onHide={() => setCategoryForm(null)}
        category={categoryForm?.category || null}
        parentId={categoryForm?.parentId || null}
        isLibrary={isLibrary}
        onSaved={handleCategorySaved}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete question?"
        message={deleteTarget ? (
          <>This permanently deletes <strong>“{deleteTarget.questionText?.slice(0, 110)}{deleteTarget.questionText?.length > 110 ? "…" : ""}”</strong>.</>
        ) : "This permanently deletes the question."}
        confirmText="Delete"
        variant="danger"
      />
      <ConfirmModal
        show={bulkConfirm}
        onHide={() => setBulkConfirm(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${plural(selected.length, "question")}?`}
        message={`This permanently deletes the ${plural(selected.length, "selected question")}. It cannot be undone.`}
        confirmText={bulkDeleting ? "Deleting…" : "Delete"}
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
}
